import React, { useEffect, useRef } from "react";
import lightLogo from "@packages/assets/swann-icon.png";
import darkLogo from "@packages/assets/swann-icon.png";
import { useTheme } from "../../hooks/useTheme";

interface WaterLoaderProps {
  progress: number;
  size?: number;
  duration?: number; // in milliseconds
  borderColor?: string;
}

const WaterLoader: React.FC<WaterLoaderProps> = ({
  progress,
  size = 200,
  duration = 2000, // default 2 seconds
  borderColor,
}) => {
  const { theme } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const dropletsRef = useRef<
    Array<{
      x: number;
      y: number;
      radius: number;
      speed: number;
      opacity: number;
      angle?: number;
      rotationSpeed?: number;
    }>
  >([]);
  const animationFrameRef = useRef<number>();
  const lastTimeRef = useRef(0);
  const waveOffsetRef = useRef(0);
  const currentProgressRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr * 2;
    canvas.height = size * dpr * 2;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr * 2, dpr * 2);

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    if (!logoRef.current) {
      const logoImg = new Image();
      logoImg.src = theme === "light" ? lightLogo : darkLogo;
      logoRef.current = logoImg;
    }

    const animate = (currentTime: number) => {
      if (!ctx || !logoRef.current) return;

      const deltaTime = currentTime - lastTimeRef.current;
      lastTimeRef.current = currentTime;

      // Smooth progress animation based on duration
      const progressDiff = progress - currentProgressRef.current;
      if (Math.abs(progressDiff) > 0.01) {
        const progressStep = (deltaTime / duration) * 50;
        const step =
          progressDiff > 0
            ? Math.min(progressStep, progressDiff)
            : Math.max(-progressStep, progressDiff);

        currentProgressRef.current += step;
      }

      const progressWave = Math.sin(waveOffsetRef.current * 2) * 4;
      const fillHeight =
        (currentProgressRef.current / 100) * size +
        progressWave * (1 - Math.abs(currentProgressRef.current - 50) / 50);

      waveOffsetRef.current += 0.01;

      ctx.clearRect(0, 0, size, size);

      // Enhanced glow effect around the entire loader
      const outerGlow = ctx.createRadialGradient(
        size / 2,
        size / 2,
        0,
        size / 2,
        size / 2,
        size * 0.8,
      );
      outerGlow.addColorStop(
        0,
        theme === "light"
          ? "rgba(251, 191, 36, 0.1)"
          : "rgba(251, 191, 36, 0.2)",
      );
      outerGlow.addColorStop(0.5, "rgba(251, 191, 36, 0.05)");
      outerGlow.addColorStop(1, "rgba(251, 191, 36, 0)");

      ctx.fillStyle = outerGlow;
      ctx.fillRect(0, 0, size, size);

      // Logo outline - sadece iç kısım
      ctx.save();
      ctx.drawImage(logoRef.current, 0, 0, size, size);
      ctx.globalCompositeOperation = "source-in";
      ctx.fillStyle = "transparent";
      ctx.fillRect(0, 0, size, size);
      ctx.restore();

      // Water effect - sadece iç kısım
      ctx.save();
      ctx.drawImage(logoRef.current, 0, 0, size, size);
      ctx.globalCompositeOperation = "source-atop";

      // Create water mask with smooth edges
      ctx.beginPath();
      ctx.rect(0, size - fillHeight, size, fillHeight);
      ctx.clip();

      // Enhanced water gradient with more vibrant colors
      const waterGradient = ctx.createLinearGradient(
        0,
        size - fillHeight,
        0,
        size,
      );

      // More vibrant and modern color palette
      waterGradient.addColorStop(0, "#FFF8E1"); // Light cream
      waterGradient.addColorStop(0.1, "#FFE082"); // Soft yellow
      waterGradient.addColorStop(0.25, "#FFB74D"); // Warm orange
      waterGradient.addColorStop(0.4, "#FF9800"); // Vibrant orange
      waterGradient.addColorStop(0.6, "#FB8C00"); // Deep orange
      waterGradient.addColorStop(0.8, "#F57C00"); // Rich orange
      waterGradient.addColorStop(0.9, "#EF6C00"); // Intense orange
      waterGradient.addColorStop(1, "#E65100"); // Dark orange

      ctx.fillStyle = waterGradient;
      ctx.fillRect(0, 0, size, size);

      // Enhanced wave pattern with more complex and natural movement
      ctx.beginPath();
      ctx.moveTo(0, size);

      // More complex wave system with multiple frequencies
      for (let i = 0; i <= size; i += 0.5) {
        const normalizedHeight = (size - (size - fillHeight)) / size;
        const amplitudeFactor = Math.min(1, normalizedHeight * 2);

        // Multi-frequency wave system
        const waveHeight =
          (Math.sin(i * 0.008 + waveOffsetRef.current) * 12 +
            Math.sin(i * 0.015 + waveOffsetRef.current * 1.5) * 8 +
            Math.sin(i * 0.003 + waveOffsetRef.current * 0.8) * 15 +
            Math.sin(i * 0.025 + waveOffsetRef.current * 2.2) * 6) *
          amplitudeFactor;

        // Enhanced top line wave
        const topLineWave =
          Math.sin(i * 0.02 + waveOffsetRef.current * 1.8) * 8 +
          Math.sin(i * 0.04 + waveOffsetRef.current * 1.2) * 5 +
          Math.sin(i * 0.01 + waveOffsetRef.current * 0.6) * 10;

        // Improved edge smoothing
        const edgeFactor = Math.min(i, size - i) / (size * 0.15);
        const smoothing = Math.min(1, edgeFactor);

        ctx.lineTo(
          i,
          size - fillHeight + (waveHeight + topLineWave) * smoothing * 0.6,
        );
      }

      // Enhanced reflection effect with multiple layers
      const reflection = ctx.createLinearGradient(
        0,
        size - fillHeight,
        0,
        size,
      );
      reflection.addColorStop(0, "rgba(255, 255, 255, 0.4)"); // Stronger top reflection
      reflection.addColorStop(0.2, "rgba(255, 255, 255, 0.3)");
      reflection.addColorStop(0.4, "rgba(255, 255, 255, 0.2)");
      reflection.addColorStop(0.6, "rgba(255, 255, 255, 0.1)");
      reflection.addColorStop(0.8, "rgba(255, 255, 255, 0.05)");
      reflection.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.lineTo(size, size);
      ctx.closePath();
      ctx.fill();

      // Primary reflection layer
      ctx.fillStyle = reflection;
      ctx.fill();

      // Dynamic secondary reflection layer
      const reflection2 = ctx.createLinearGradient(
        0,
        size - fillHeight,
        0,
        size,
      );
      reflection2.addColorStop(
        0,
        `rgba(255, 255, 255, ${Math.abs(Math.sin(waveOffsetRef.current * 0.5)) * 0.15})`,
      );
      reflection2.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.fillStyle = reflection2;
      ctx.fill();

      // Enhanced droplet system with better physics
      if (Math.random() < 0.06 && currentProgressRef.current < 95) {
        createDroplet();
      }

      dropletsRef.current = dropletsRef.current.filter((droplet) => {
        if (droplet.y > size - fillHeight) {
          // Enhanced splash effect with more particles
          for (let i = 0; i < 6; i++) {
            dropletsRef.current.push({
              x: droplet.x + (Math.random() - 0.5) * 25,
              y: size - fillHeight,
              radius: droplet.radius * 0.4,
              speed: -droplet.speed * 0.7,
              opacity: 0.9,
              angle: Math.random() * Math.PI * 2,
              rotationSpeed: (Math.random() - 0.5) * 0.12,
            });
          }
          return false;
        }

        ctx.save();
        if (droplet.angle !== undefined) {
          ctx.translate(droplet.x, droplet.y);
          ctx.rotate(droplet.angle);
          ctx.translate(-droplet.x, -droplet.y);
          if (droplet.rotationSpeed) droplet.angle += droplet.rotationSpeed;
        }

        // Enhanced droplet glow with better colors
        const dropletGradient = ctx.createRadialGradient(
          droplet.x,
          droplet.y,
          0,
          droplet.x,
          droplet.y,
          droplet.radius * 3,
        );
        dropletGradient.addColorStop(
          0,
          `rgba(255, 248, 225, ${droplet.opacity})`,
        );
        dropletGradient.addColorStop(
          0.3,
          `rgba(255, 224, 130, ${droplet.opacity * 0.9})`,
        );
        dropletGradient.addColorStop(
          0.6,
          `rgba(255, 183, 77, ${droplet.opacity * 0.6})`,
        );
        dropletGradient.addColorStop(1, `rgba(255, 183, 77, 0)`);

        ctx.beginPath();
        ctx.fillStyle = dropletGradient;
        ctx.arc(droplet.x, droplet.y, droplet.radius * 3, 0, Math.PI * 2);
        ctx.fill();

        // Actual droplet with enhanced shine
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 248, 225, ${droplet.opacity})`;
        ctx.arc(droplet.x, droplet.y, droplet.radius, 0, Math.PI * 2);
        ctx.fill();

        // Enhanced shine effect
        const shineGradient = ctx.createLinearGradient(
          droplet.x - droplet.radius,
          droplet.y - droplet.radius,
          droplet.x + droplet.radius,
          droplet.y + droplet.radius,
        );
        shineGradient.addColorStop(
          0,
          `rgba(255, 255, 255, ${droplet.opacity * 0.7})`,
        );
        shineGradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
        ctx.fillStyle = shineGradient;
        ctx.fill();

        ctx.restore();

        droplet.y += droplet.speed * 0.6; // Slower droplets for better visibility
        droplet.opacity -= 0.006; // Slower fade for longer visibility

        return droplet.opacity > 0;
      });

      ctx.restore();

      // Enhanced glow effect with multiple layers
      const glowGradient1 = ctx.createRadialGradient(
        size / 2,
        size / 2,
        0,
        size / 2,
        size / 2,
        size / 2,
      );
      glowGradient1.addColorStop(
        0,
        theme === "light"
          ? "rgba(255, 248, 225, 0.3)"
          : "rgba(251, 191, 36, 0.3)",
      );
      glowGradient1.addColorStop(
        0.5,
        theme === "light"
          ? "rgba(255, 224, 130, 0.15)"
          : "rgba(245, 158, 11, 0.15)",
      );
      glowGradient1.addColorStop(
        1,
        theme === "light" ? "rgba(251, 191, 36, 0)" : "rgba(161, 98, 7, 0)",
      );

      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = glowGradient1;
      ctx.fillRect(0, 0, size, size);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const createDroplet = () => {
      if (dropletsRef.current.length < 12) {
        // Fewer but more impactful droplets
        dropletsRef.current.push({
          x: Math.random() * size,
          y: -15,
          radius: Math.random() * 2.5 + 1.5,
          speed: Math.random() * 1.5 + 1.2, // Slower droplets
          opacity: 1,
          angle: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.05,
        });
      }
    };

    if (logoRef.current.complete) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      logoRef.current.onload = () => {
        animationFrameRef.current = requestAnimationFrame(animate);
      };
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [progress, size, duration]);

  return (
    <div className="relative mb-8">
      <canvas
        ref={canvasRef}
        className="rounded-full shadow-lg"
        style={{
          filter: "drop-shadow(0 10px 25px rgba(251, 191, 36, 0.3))",
        }}
      />
    </div>
  );
};

export default WaterLoader;
